/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { debounce } from 'lodash';
import type { Feature, MapBrowserEvent } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import { Vector } from 'ol/layer';
import { unByKey } from 'ol/Observable';
import VectorSource from 'ol/source/Vector';
import type { StyleLike } from 'ol/style/Style';
import React, { useContext, useEffect, useRef, useState } from 'react';
import MapContext from '../context/MapContext';
import { useDragBoxSelect } from '../utils/dragBoxSelect';
import {
  isDefaultModifiable,
  useModifyFeatures,
} from '../utils/modifyFeatures';
import { isDefaultMovable, useMoveFeatures } from '../utils/moveFeatures';
import type {
  BoxSelect,
  ClickOnFeature,
  HoverSelect,
  ModifyFeatures,
  MoveFeatures,
} from '../utils/types';

export interface FeatureLayerProps {
  features: Feature[];
  style: StyleLike;
  zIndex?: number;
  hoverSelect?: HoverSelect;
  clickOnFeature?: ClickOnFeature;
  boxSelect?: BoxSelect;
  moveFeatures?: MoveFeatures;
  modifyFeatures?: ModifyFeatures;
  layerId?: string;
  testId?: string;
  opacity?: number;
  declutter?: boolean;
  visible?: boolean;
}

export const FeatureLayer: React.FC<FeatureLayerProps> = ({
  features,
  style,
  zIndex,
  hoverSelect,
  clickOnFeature,
  boxSelect,
  moveFeatures,
  modifyFeatures,
  layerId,
  testId,
  opacity,
  declutter = false,
  visible = true,
}: FeatureLayerProps) => {
  const { map } = useContext(MapContext);
  const [source, setSource] = useState<VectorSource>();
  const isInteractingRef = useRef(false);
  const pendingFeaturesRef = useRef<Feature[] | undefined>(undefined);

  const layerRef =
    useRef<Vector<VectorSource<Feature<Geometry>>, Feature<Geometry>>>(
      undefined,
    );

  useEffect(() => {
    if (!map || !visible) {
      return (): void => {};
    }
    const source = new VectorSource<Feature>({
      features: [],
    });

    const layer = new Vector({
      source,
      zIndex,
      style,
      properties: {
        layerId,
        testId,
      },
      opacity,
      declutter,
      visible,
    });
    layerRef.current = layer;

    layer.set('layerType', 'FeatureLayer');

    setSource(source);
    map.addLayer(layer);

    return (): void => {
      setSource(undefined);
      source.clear();
      map.removeLayer(layer);
    };
    // NOTE: style should not be part of this effect, as it changes always, causing the layer to be add/removed continuousely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerId, map, zIndex, testId, visible]);

  useEffect(() => {
    // Handle style function:
    if (layerRef.current && style) {
      layerRef.current?.setStyle(style);
    }
  }, [style]);

  useEffect(() => {
    // Handle opacity:
    if (layerRef.current && opacity !== undefined) {
      layerRef.current?.setOpacity(opacity);
    }
  }, [opacity]);

  // Layer visible useEffect
  useEffect(() => {
    layerRef.current?.setVisible(visible);
  }, [visible]);

  const syncSource = React.useCallback(
    (nextFeatures: Feature[]): void => {
      if (!source) {
        return;
      }
      source.clear();
      if (nextFeatures.length > 0) {
        source.addFeatures(nextFeatures);
      }
    },
    [source],
  );

  useEffect(() => {
    if (!source) {
      return;
    }
    // Rebuilding the source mid-drag would swap the features out from under the
    // move or modify interaction, so it is deferred until the drag has finished.
    if (isInteractingRef.current) {
      pendingFeaturesRef.current = features;
      return;
    }
    syncSource(features ?? []);
  }, [source, features, syncSource]);

  useEffect((): (() => void) => {
    if (!map || !source || !hoverSelect || !hoverSelect.active || !visible) {
      return (): void => {};
    }

    const previousHover: FeatureLike[] = [];

    const pointerMoveFunction = debounce(
      (evt: MapBrowserEvent<KeyboardEvent | WheelEvent | PointerEvent>) => {
        if (isInteractingRef.current) {
          return;
        }
        const hoveredFeature = map.forEachFeatureAtPixel(
          evt.pixel,
          (feature: FeatureLike, layer): FeatureLike | false => {
            if (
              !layer ||
              layer?.getSource() !== source ||
              feature.get('isHoverDisabled')
            ) {
              return false;
            }
            return feature;
          },
        );

        if (hoveredFeature && previousHover.includes(hoveredFeature)) {
          return;
        }

        const unhovered = previousHover.length > 0;

        previousHover.forEach((feature) => {
          (feature as Feature).set('hover', false);
        });
        previousHover.splice(0);

        if (hoveredFeature) {
          previousHover.push(hoveredFeature);
          (hoveredFeature as Feature).set('hover', true);
          hoverSelect.onHover?.(hoveredFeature as Feature);
        } else if (unhovered) {
          hoverSelect.onHover?.(undefined);
        }
      },
      5,
      {},
    );

    const eventKey = map.on('pointermove', pointerMoveFunction);

    return () => {
      pointerMoveFunction.cancel();
      source.getFeatures().forEach((feature) => {
        (feature as Feature).set('hover', false);
      });
      unByKey(eventKey);
      hoverSelect.onHover?.(undefined);
    };
  }, [map, source, hoverSelect, visible]);

  useEffect((): (() => void) => {
    if (!map || !source || !clickOnFeature || !visible) {
      return (): void => {};
    }

    const clickFunction = (
      evt: MapBrowserEvent<KeyboardEvent | WheelEvent | PointerEvent>,
    ): void => {
      const features: Feature[] = [];
      map.forEachFeatureAtPixel(
        evt.pixel,
        (feature, layer) => {
          if (layer?.getSource() !== source) {
            return;
          }
          features.push(feature as Feature);
        },
        { hitTolerance: clickOnFeature.hitTolerance },
      );

      const isShiftClick =
        (evt.originalEvent as PointerEvent).shiftKey ?? false;
      clickOnFeature.onClick(features, isShiftClick);
    };

    const eventKey = map.on(clickOnFeature.eventType ?? 'click', clickFunction);

    return (): void => {
      unByKey(eventKey);
    };
  }, [map, source, clickOnFeature, visible]);

  useDragBoxSelect({
    map,
    source,
    active: Boolean(boxSelect?.active && visible),
    onBoxSelect: boxSelect?.onBoxSelect,
  });

  const handleInteractionStart = React.useCallback((): void => {
    isInteractingRef.current = true;
  }, []);

  const flushPendingFeatures = React.useCallback((): void => {
    isInteractingRef.current = false;
    const pendingFeatures = pendingFeaturesRef.current;
    pendingFeaturesRef.current = undefined;
    if (pendingFeatures) {
      syncSource(pendingFeatures);
    }
  }, [syncSource]);

  const handleMoveEnd = React.useCallback(
    (movedFeatures: Feature[]): void => {
      flushPendingFeatures();
      if (movedFeatures.length > 0) {
        moveFeatures?.onMoveEnd(movedFeatures);
      }
    },
    [moveFeatures, flushPendingFeatures],
  );

  const handleModifyEnd = React.useCallback(
    (modifiedFeatures: Feature[]): void => {
      flushPendingFeatures();
      if (modifiedFeatures.length > 0) {
        modifyFeatures?.onModifyEnd(modifiedFeatures);
      }
    },
    [modifyFeatures, flushPendingFeatures],
  );

  useMoveFeatures({
    map,
    features,
    active: Boolean(moveFeatures?.active && visible),
    isMovable: moveFeatures?.isMovable ?? isDefaultMovable,
    onMoveEnd: handleMoveEnd,
    onMoveStart: handleInteractionStart,
  });

  // Added after the move interaction so a vertex grab wins over a group move.
  useModifyFeatures({
    map,
    features,
    active: Boolean(modifyFeatures?.active && visible),
    isModifiable: modifyFeatures?.isModifiable ?? isDefaultModifiable,
    onModifyEnd: handleModifyEnd,
    onModifyStart: handleInteractionStart,
  });

  return null;
};
